import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import Layout from "../layouts/Layout";
import {
  API_BASE_URL,
  FONT_HEADING,
  FONT_UI,
  SKIN_THEMES,
} from "../layouts/constants";

function useSkin() {
  const skinId = localStorage.getItem("skinTheme") || "navy";
  return SKIN_THEMES.find((s) => s.id === skinId) || SKIN_THEMES[0];
}

function getToken() {
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("adminToken")
  );
}

export default function CustomReports() {
  const navigate = useNavigate();
  const skin = useSkin();

  const [routes, setRoutes] = useState([]);
  const [guards, setGuards] = useState([]);
  const [checkpoints, setCheckpoints] = useState([]);
  const [scanningRecords, setScanningRecords] = useState([]);
  const [incidentsData, setIncidentsData] = useState([]);

  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    guard: "",
    route: "",
    incidents: "",
  });

  const [filteredPatrols, setFilteredPatrols] = useState([]);
  const [filteredIncidents, setFilteredIncidents] = useState([]);

  const [stats, setStats] = useState({
    totalPatrols: 0,
    totalIncidents: 0,
    completionRate: 0,
    avgTime: 0,
  });

  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    company_name: "",
    licensedFor: "",
    admin_active: 0,
    company_address: "",
    company_phone: "",
    admin_email: "",
    company_logo: "",
    user_map: "",
  });

  useEffect(() => {
    const token = getToken();
    const isLoggedIn = localStorage.getItem("isLoggedIn");
    if (!token || !isLoggedIn) {
      navigate("/");
      return;
    }
    fetchDashboardData(token);
    fetchData(token);
  }, []);

  const fetchDashboardData = async (token) => {
    try {
      const res = await fetch(`${API_BASE_URL}/dashboard/admin/dashboard`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.ok) {
        const data = await res.json();
        const companyDetails = data?.data?.companyDetails || {};
        setDashboardData(companyDetails);
      }
    } catch (error) {
      console.error("Dashboard fetch error:", error);
    }
  };

  const fetchData = async (token) => {
    try {
      setLoading(true);
      const token = getToken();

      const res = await fetch(`${API_BASE_URL}/reports/custom`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        throw new Error("Failed to fetch data");
      }

      const data = await res.json();

      setRoutes(data.routes || []);
      setGuards(data.guards || []);
      setCheckpoints(data.checkpoints || []);
      setScanningRecords(data.scanning_records || []);
      setIncidentsData(data.incidents || []);

      setFilteredPatrols(data.scanning_records || []);
      setFilteredIncidents(data.incidents || []);

      updateStats(data.scanning_records || [], data.incidents || []);
      
      toast.success("Data loaded successfully!");
    } catch (error) {
      console.error("Custom reports fetch error:", error);
      toast.error(error.message || "Failed to load reports data");
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    const { startDate, endDate, guard, route, incidents } = filters;

    let patrols = scanningRecords.filter((r) => {
      const d = r.scan_date;

      if (startDate && d < startDate) return false;
      if (endDate && d > endDate) return false;
      if (guard && String(r.guard_id) !== String(guard)) return false;
      if (route && String(r.route_id) !== String(route)) return false;

      return true;
    });

    let incs = incidentsData.filter((i) => {
      const d = i.incident_date;

      if (startDate && d < startDate) return false;
      if (endDate && d > endDate) return false;
      if (guard && String(i.guard_id) !== String(guard)) return false;
      if (route && String(i.route_id) !== String(route)) return false;

      return true;
    });

    if (incidents === "with") {
      patrols = patrols.filter((r) =>
        incs.some(
          (i) =>
            String(i.guard_id) === String(r.guard_id) &&
            String(i.checkpoint_id) === String(r.checkpoint_id)
        )
      );
    }

    if (incidents === "without") {
      patrols = patrols.filter(
        (r) =>
          !incs.some(
            (i) =>
              String(i.guard_id) === String(r.guard_id) &&
              String(i.checkpoint_id) === String(r.checkpoint_id)
          )
      );
    }

    setFilteredPatrols(patrols);
    setFilteredIncidents(incs);
    updateStats(patrols, incs);
    
    toast.success(`Filters applied: ${patrols.length} patrols, ${incs.length} incidents`);
  };

  const updateStats = (patrols, incs) => {
    const unique = new Set(patrols.map((p) => `${p.guard_id}-${p.route_id}`));

    setStats({
      totalPatrols: patrols.length,
      totalIncidents: incs.length,
      completionRate:
        patrols.length > 0 ? Math.round((unique.size / patrols.length) * 100) : 0,
      avgTime: patrols.length > 0 ? 30 : 0,
    });
  };

  const resetFilters = () => {
    setFilters({
      startDate: "",
      endDate: "",
      guard: "",
      route: "",
      incidents: "",
    });

    setFilteredPatrols(scanningRecords);
    setFilteredIncidents(incidentsData);
    updateStats(scanningRecords, incidentsData);
    
    toast.info("Filters reset successfully");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters({ ...filters, [name]: value });
  };

  const routePage = useMemo(
    () => (dashboardData.user_map === "google" ? "/route-master" : "/route-master1"),
    [dashboardData.user_map]
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--page-bg)] flex flex-col items-center justify-center gap-3">
        <div className="w-9 h-9 border-3 border-[var(--accent-faint)] border-t-[var(--accent)] rounded-full animate-spin" />
        <p className="text-sm text-[var(--text-muted)] font-['Inter',system-ui,sans-serif]">
          Loading reports...
        </p>
      </div>
    );
  }

  return (
    <Layout
      dashboardData={dashboardData}
      routePage={routePage}
      activePage="customReports"
    >
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 5000,
          style: {
            background: "var(--card-bg)",
            color: "var(--text-primary)",
            border: "1px solid var(--card-border)",
            borderRadius: "12px",
            padding: "16px 20px",
            fontSize: "13px",
            fontWeight: "600",
            boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
          },
          success: {
            iconTheme: {
              primary: "#22c55e",
              secondary: "white",
            },
          },
          error: {
            iconTheme: {
              primary: "#ef4444",
              secondary: "white",
            },
          },
          info: {
            iconTheme: {
              primary: "#3b82f6",
              secondary: "white",
            },
          },
        }}
      />

      <div className="font-['Inter',system-ui,sans-serif] text-[var(--text-primary)]">
        <h1 className="text-[22px] font-extrabold text-[var(--text-primary)] tracking-tight m-0">
          Custom Reports
        </h1>
        <p className="text-[13px] text-[var(--text-muted)] m-0 mb-[22px]">
          Advanced filtering and analytics for patrol and incidents
        </p>

        {/* Top Card */}
        <div className="relative overflow-hidden bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-[var(--accent)]" />
          <div className="flex items-center justify-between gap-3.5 flex-wrap">
            <div>
              <h2 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0">
                Custom Filters
              </h2>
              <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
                Apply advanced filters to refine your reports
              </p>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className="block text-[11px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                Start Date
              </label>
              <input
                type="date"
                name="startDate"
                value={filters.startDate}
                onChange={handleChange}
                className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
                style={{ fontFamily: FONT_UI }}
              />
            </div>

            <div>
              <label className="block text-[11px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                End Date
              </label>
              <input
                type="date"
                name="endDate"
                value={filters.endDate}
                onChange={handleChange}
                className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
                style={{ fontFamily: FONT_UI }}
              />
            </div>

            <div>
              <label className="block text-[11px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                Guard
              </label>
              <select
                name="guard"
                value={filters.guard}
                onChange={handleChange}
                className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
                style={{ fontFamily: FONT_UI }}
              >
                <option value="">All Guards</option>
                {guards.map((g) => (
                  <option key={g.id || g.member_id} value={g.id || g.member_id}>
                    {g.name || g.guard_name || g.fld_name || "Unknown"}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-[11px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                Route
              </label>
              <select
                name="route"
                value={filters.route}
                onChange={handleChange}
                className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
                style={{ fontFamily: FONT_UI }}
              >
                <option value="">All Routes</option>
                {routes.map((r) => (
                  <option key={r.route_id || r.id} value={r.route_id || r.id}>
                    {r.r_name || r.route_name || "Unknown"}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-[11px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                Incident Filter
              </label>
              <select
                name="incidents"
                value={filters.incidents}
                onChange={handleChange}
                className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
                style={{ fontFamily: FONT_UI }}
              >
                <option value="">All</option>
                <option value="with">With Incidents</option>
                <option value="without">Without Incidents</option>
              </select>
            </div>

            <div className="flex items-end gap-2">
              <button
                onClick={applyFilters}
                className="flex-1 border-0 rounded-xl px-4 py-3 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-95 hover:-translate-y-0.5"
                style={{
                  background: "var(--btn-bg)",
                  color: "var(--btn-text)",
                  boxShadow: "0 4px 18px var(--accent-glow)",
                }}
              >
                Apply
              </button>
              <button
                onClick={resetFilters}
                className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-4 py-3 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-90 hover:-translate-y-0.5"
              >
                Reset
              </button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
          <h3 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0 mb-4">
            Statistics
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard label="Total Patrols" value={stats.totalPatrols} color="blue" />
            <StatCard label="Total Incidents" value={stats.totalIncidents} color="red" />
            <StatCard label="Completion Rate" value={`${stats.completionRate}%`} color="green" />
            <StatCard label="Avg Time" value={`${stats.avgTime} min`} color="amber" />
          </div>
        </div>

        {/* Patrol Records Table */}
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
          <h3 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0 mb-4">
            Patrol Records
            <span className="ml-2 px-2.5 py-0.5 rounded-full bg-[var(--accent-faint)] text-[var(--accent)] text-[11px] font-extrabold">
              {filteredPatrols.length}
            </span>
          </h3>

          <div className="overflow-x-auto rounded-xl border border-[var(--surface-border)]">
            <table className="w-full min-w-[700px] text-[13px] font-['Inter',system-ui,sans-serif]">
              <thead>
                <tr style={{ background: "var(--btn-bg)" }}>
                  <Th>Date</Th>
                  <Th>Time</Th>
                  <Th>Guard ID</Th>
                  <Th>Guard</Th>
                  <Th>Route</Th>
                  <Th>Checkpoint</Th>
                </tr>
              </thead>
              <tbody>
                {filteredPatrols.length ? (
                  filteredPatrols.map((r, i) => (
                    <tr
                      key={i}
                      className={`border-b border-[var(--divider)] hover:bg-[var(--accent-faint)] transition-colors ${
                        i % 2 === 0 ? "bg-[var(--card-bg)]" : "bg-[var(--surface)]"
                      }`}
                    >
                      <Td>{r.scan_date || "-"}</Td>
                      <Td>{r.scan_time || "-"}</Td>
                      <Td>{r.guard_id || "-"}</Td>
                      <Td>{r.guard_name || "-"}</Td>
                      <Td>{r.route_name || "-"}</Td>
                      <Td>{r.checkpoint_name || "-"}</Td>
                    </tr>
                  ))
                ) : (
                  <EmptyRow col={6} />
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Incident Records Table */}
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
          <h3 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0 mb-4">
            Incident Records
            <span className="ml-2 px-2.5 py-0.5 rounded-full bg-[var(--accent-faint)] text-[var(--accent)] text-[11px] font-extrabold">
              {filteredIncidents.length}
            </span>
          </h3>

          <div className="overflow-x-auto rounded-xl border border-[var(--surface-border)]">
            <table className="w-full min-w-[800px] text-[13px] font-['Inter',system-ui,sans-serif]">
              <thead>
                <tr style={{ background: "var(--btn-bg)" }}>
                  <Th>Date</Th>
                  <Th>Time</Th>
                  <Th>Guard ID</Th>
                  <Th>Guard</Th>
                  <Th>Route</Th>
                  <Th>Checkpoint</Th>
                  <Th>Title</Th>
                  <Th>Severity</Th>
                </tr>
              </thead>
              <tbody>
                {filteredIncidents.length ? (
                  filteredIncidents.map((i, idx) => (
                    <tr
                      key={idx}
                      className={`border-b border-[var(--divider)] hover:bg-[var(--accent-faint)] transition-colors ${
                        idx % 2 === 0 ? "bg-[var(--card-bg)]" : "bg-[var(--surface)]"
                      }`}
                    >
                      <Td>{i.incident_date || "-"}</Td>
                      <Td>{i.incident_time || "-"}</Td>
                      <Td>{i.guard_id || "-"}</Td>
                      <Td>{i.guard_name || "-"}</Td>
                      <Td>{i.route_name || "-"}</Td>
                      <Td>{i.checkpoint_name || "-"}</Td>
                      <Td>{i.title || "-"}</Td>
                      <Td>
                        <SeverityBadge value={i.severity} />
                      </Td>
                    </tr>
                  ))
                ) : (
                  <EmptyRow col={8} />
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
}

/* ================= UI COMPONENTS ================= */

function StatCard({ label, value, color }) {
  const colorMap = {
    blue: "bg-[rgba(59,130,246,0.12)] text-[#3b82f6]",
    green: "bg-[rgba(16,185,129,0.12)] text-[#10b981]",
    red: "bg-[rgba(239,68,68,0.12)] text-[#ef4444]",
    amber: "bg-[rgba(245,158,11,0.12)] text-[#f59e0b]",
    purple: "bg-[rgba(139,92,246,0.12)] text-[#8b5cf6]",
  };
  return (
    <div className={`rounded-xl px-4 py-4 border ${colorMap[color] || colorMap.blue}`}>
      <p className="text-[10px] font-extrabold uppercase tracking-wider opacity-75 m-0" style={{ fontFamily: FONT_UI }}>
        {label}
      </p>
      <p className="text-[26px] font-extrabold m-0 mt-0.5" style={{ fontFamily: FONT_HEADING }}>
        {value}
      </p>
    </div>
  );
}

function SeverityBadge({ value }) {
  if (!value) return <span className="text-[var(--text-muted)] text-[13px]">-</span>;
  const v = String(value).toLowerCase();
  const styles = {
    critical: "bg-[rgba(220,38,38,0.12)] border border-[rgba(220,38,38,0.28)] text-[#dc2626]",
    high: "bg-[rgba(239,68,68,0.12)] border border-[rgba(239,68,68,0.28)] text-[#ef4444]",
    medium: "bg-[rgba(245,158,11,0.12)] border border-[rgba(245,158,11,0.28)] text-[#f59e0b]",
    low: "bg-[rgba(16,185,129,0.12)] border border-[rgba(16,185,129,0.28)] text-[#10b981]",
  };
  return (
    <span
      className={`px-2.5 py-1 rounded-full text-[11px] font-extrabold ${styles[v] || "bg-[var(--surface)] text-[var(--text-muted)]"}`}
      style={{ fontFamily: FONT_UI }}
    >
      {value}
    </span>
  );
}

function Th({ children }) {
  return (
    <th className="px-4 py-3 text-left text-[11px] font-extrabold uppercase tracking-wider text-white whitespace-nowrap">
      {children}
    </th>
  );
}

function Td({ children }) {
  return (
    <td className="px-4 py-3 text-[13px] text-[var(--text-primary)] whitespace-nowrap">
      {children}
    </td>
  );
}

function EmptyRow({ col }) {
  return (
    <tr>
      <td colSpan={col} className="text-center py-8 text-[var(--text-muted)] text-[13px]">
        No data available
      </td>
    </tr>
  );
}